import React from "react";
import intl from "react-intl-universal";
import {override, makeObservable} from "mobx";
import {WorkspaceUser} from '../../API';
import {FilterSchema} from "./DataSource";
import {TableDataSource} from "./TableDataSource";
import {ColId, ColSchema, Row} from "../../ui/components/Table";

import {listWorkspaceUsersByWsId} from "./requests/listWorkspaceUsers";
import {formatDate} from "../../utils/date";
import {UserCard} from "../../ui/components/UserCard";
import {WsUserActions} from "../../ui/screens/WorkspaceSettings/WorkspaceRemoveUser";
import {Tooltip} from "../../ui/components/Tooltip";
import {getFullName, getFullNameOrEmpty} from "../../utils/userHelper";

import {onCreateWorkspaceUser, onDeleteWorkspaceUser, onUpdateWorkspaceUser} from "../../graphql/subscriptions";
import {FullDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {AccessRolesDataSource} from "./AccessRolesDataSource";
import {UserRolesDataSource} from "./UserRolesDataSource";
import {MODALS} from "../../ui/screens/modals/modal-names";

const colOrder: ColId[] = [
    'name',
    'email',
    'lastActive',
    'role',
    'accessRoles',
    'actions',
];

const colSchema: ColSchema = {
    name: {
        getTitle: () => intl.get('members.tableCols.name').d('Name'),
        alignLeft: true,
        minWidth: 180,
        maxWidth: 320,
    },
    email: {
        minWidth: 120,
        maxWidth: 240,
        getTitle: () => intl.get('members.tableCols.email').d('Email'),
        alignLeft: true
    },
    lastActive: {
        minWidth: 80,
        maxWidth: 160,
        getTitle: () => intl.get('members.tableCols.lastActive').d('Last Active'),
        alignLeft: true
    },
    role: {
        getTitle: () => intl.get('members.tableCols.role').d('Permission level'),
        minWidth: 120,
        maxWidth: 178,
        alignLeft: true
    },
    accessRoles: {
        getTitle: () => intl.get('members.tableCols.accessRoles').d('Roles'),
        minWidth: 220,
        maxWidth: 260,
        alignLeft: true
    },
    actions: {
        getTitle: () => '',
        minWidth: 56,
        maxWidth: 56
    },
};

export class WsUsersDataSource extends TableDataSource<WorkspaceUser, WorkspaceUser, FullDataSourceDeps> {
    readonly _modelName = 'WorkspaceUser' as const;
    readonly _subscriptionGQLs = {
        onCreate: onCreateWorkspaceUser,
        onUpdate: onUpdateWorkspaceUser,
        onDelete: onDeleteWorkspaceUser,
    } as const
    wsId: string;

    private accessRolesDS?: AccessRolesDataSource;
    private userRolesDS?: UserRolesDataSource;

    get syncHash() {
        return `ws` + this.wsId.slice(0,6);
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientForTable('workspaceUsers');
    };

    constructor(deps: FullDataSourceDeps, wsId: string) {
        super(deps);

        makeObservable(this, {
            allRows: override,
        });

        this.wsId = wsId;

        const dsManager = this.deps.userState?.dsManager;
        this.accessRolesDS = dsManager?.getAccessRolesDataSource();
        this.userRolesDS = dsManager?.getUserRolesDataSource();

        void this.accessRolesDS?.initialLoad();
        void this.userRolesDS?.initialLoad();
    }

    getColsSchema(): ColSchema {
        return colSchema;
    }

    getColOrder(): ColId[] {
        return colOrder;
    }

    get allRows() {
        return [
            {
                __typename: "WorkspaceUser",
                ownerId: this.deps.userState.tenantId,
                workspaceId: this.wsId,
                userId: this.deps.userState.ownerUserId,
                role: 'owner',
                id: '0',
                version: 0,
                _lastChangedAt: 0,
            } as WorkspaceUser,
            // The owner is always synthesized above; drop any real WorkspaceUser
            // record for them so they don't show up twice in the members table.
            ...this._allRows.filter(wsUser => wsUser.userId !== this.deps.userState.ownerUserId)
        ];
    }

    getFilterableValue = (field: string, wsUser: WorkspaceUser) => {
        const user = this.deps.userState.usersDS.users.get(wsUser.userId);
        switch (field) {
            case 'name':
                return getFullNameOrEmpty(user);
            case 'lastActive':
                return user.lastActive;
            case 'email':
                return user.email;
            case 'role':
                return this.deps.userState.getUserRole(user);
            case 'accessRoles': {
                const userRole = this.deps.userState.getUserRole(user);
                if (userRole === 'owner') {
                    return 'All access';
                }
                const roleNames = this.getUserAccessRoleNames(user.id);
                return roleNames.length ? roleNames.join(', ') : '';
            }
            default:
                return wsUser[field as keyof WorkspaceUser]
        }
    }

    private getUserAccessRoleNames(userId: string): string[] {
        if (!this.accessRolesDS || !this.userRolesDS) {
            return [];
        }
        if (!this.accessRolesDS.isInitialized || !this.userRolesDS.isInitialized) {
            return [];
        }

        const wsRoles = this.accessRolesDS.getWsRoles(this.wsId);

        const assignedRoleIds = this.userRolesDS
            .getUserRolesByUserId(userId)
            .filter(ur => ur.workspaceId === this.wsId)
            .map(ur => ur.roleId);

        const uniqueRoleIds = Array.from(new Set(assignedRoleIds));

        return uniqueRoleIds
            .map(roleId => wsRoles.find(r => r.id === roleId)?.displayName)
            .filter((name): name is string => Boolean(name))
            .sort((a, b) => a.localeCompare(b));
    }

    mapResultToTableRow = (wsUser: WorkspaceUser): Row => {
        const user = this.deps.userState.usersDS.users.get(wsUser.userId);
        const currentUser = this.deps.userState.currentUserDb
        const formattedDate = user.lastActive
            ? formatDate(this.deps.rootStore.currentLocale, new Date(user.lastActive as string))
            : {date: intl.get('members.noActivity').d('No activity'), isToday: false};
        const isUserOnline = this.deps.userState.activityProvider.isUserActive(user.id);

        const userRole = this.deps.userState.getUserRole(user);
        const userAccessRoleNames = this.getUserAccessRoleNames(user.id);
        const isAccessRolesLoading = Boolean(
            this.accessRolesDS && this.userRolesDS && (!this.accessRolesDS.isInitialized || !this.userRolesDS.isInitialized)
        );
        const isOwner = userRole === 'owner';
        const accessRolesLabel = isAccessRolesLoading
            ? intl.get('common.loading').d('Loading…')
            : (userAccessRoleNames.length ? userAccessRoleNames.join(', ') : '—');

        const renderUserRoleLabel = (userRole: string) => {
            switch (userRole) {
                case 'owner':
                    return intl.get('user-role.owner').d('Owner');
                case 'admin':
                    return intl.get('user-role.admin').d('Admin');
                case 'reader':
                    return intl.get('user-role.reader').d('Reader');
                case 'editor':
                    return intl.get('user-role.editor').d('Editor');
                default:
                    return;
            }
        };
        return {
            id: user.id,
            name: (
                <div className="Members_row-card">
                    <UserCard user={user} size={36} isEmail={false}/>&nbsp;{currentUser?.id === user.id &&
                    <span>{intl.get('members.selfUser').d('(you)')}</span>}
                </div>
            ),
            email: user.email,
            lastActive: (
                isUserOnline ?
                    <span className="Member__online">{intl.get('common.online').d('Online')}</span>
                    : formattedDate.date
            ),
            role: (
                <Tooltip
                    title={intl.getHTML('wsMembers.role.change.description').d('You can change user permission levels in <b>Company Settings > Members</b>')}
                    maxWidth={200}>
                    <div className="Members__Owner">
                        {renderUserRoleLabel(userRole)}
                    </div>
                </Tooltip>
            ),
            accessRoles: (
                isOwner
                    ? intl.get('wsMembers.accessRoles.owner').d('All access')
                    : (
                        <Tooltip title={accessRolesLabel} maxWidth={320}>
                            <button
                                type="button"
                                className="Members__access-roles"
                                onClick={() => {
                                    this.deps.userState.appState.setModal({
                                        name: MODALS.MEMBER_ROLES_EDIT,
                                        params: { userId: user.id },
                                    });
                                }}
                            >
                                {accessRolesLabel}
                            </button>
                        </Tooltip>
                    )
            ),
            actions: userRole !== 'owner' ?
                <WsUserActions user={user} wsUser={wsUser} className={"Members__delete"}/> : " ",
            raw: user,
            rawWsUser: wsUser,
        };
    }

    getFilterDefaultValues(): {} {
        return {}
    }

    getFilterSchema(): FilterSchema<WorkspaceUser> {
        return {
            closedAt: {
                type: 'client',
                apply: (wsUser: WorkspaceUser) => {
                    const user = this.deps.userState.usersDS.users.get(wsUser.userId);
                    return !user.closedAt;
                }
            },
            name: {
                type: 'client',
                apply: (wsUser: WorkspaceUser) => {
                    const user = this.deps.userState.usersDS.users.get(wsUser.userId);
                    return getFullName(user)?.toLowerCase().includes(this.filterValues.name?.toLowerCase() || '')
                }
            },
            role: {
                type: 'client',
                apply: (wsUser: WorkspaceUser) => {
                    const user = this.deps.userState.usersDS.users.get(wsUser.userId);
                    return this.filterValues.role ? this.deps.userState.getUserRole(user) === this.filterValues.role : true
                }
            }
        };
    }

    loadGQLFetch() {
        return listWorkspaceUsersByWsId(
            this.deps.dataClientService,
            this.deps.authService,
            this.deps.userState.tenantId,
            this.wsId
        );
    };

    loadMoreGQLFetch(nextToken: string) {
        return listWorkspaceUsersByWsId(
            this.deps.dataClientService,
            this.deps.authService,
            this.deps.userState.tenantId,
            this.wsId, 
            undefined, 
            nextToken
        );
    };
}
